/**
 * CDM Adapter Specs — Track 1.1: generic JSON-API adapter management.
 *
 * Each row is a YAML/JSON spec that the engine treats as a full adapter
 * (REGISTRY entry). Admins can add a new commercial tool by editing a
 * spec here instead of writing Python.
 *
 * Shipped specs (Snipe-IT, ManageEngine SDP) come from
 * backend/cdm/specs/*.yml; DB rows override by name.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Code, RefreshCw, ChevronLeft, Plus, Trash2, Play, FileCode,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface SpecSummary {
  id: string
  name: string
  display_name: string
  archetype: string
  enabled: boolean
  kind?: string
  endpoint: string
  auth_kind: string
  field_count: number
  match_keys: string[]
  notes: string | null
  updated_at: string
}

type SpecKind = 'json_api' | 'sql'

const STARTER_SPEC_JSON = {
  name: 'my-new-source',
  display_name: 'My New Source',
  archetype: 'asset_laptop',
  kind: 'json_api',
  endpoint: 'https://api.example.com/v1',
  records_path: '/devices',
  records_root: '$.value',
  id_path: '$.id',
  auth: { kind: 'bearer', bearer_field: 'token' },
  pagination: { kind: 'page_param', per_page: 100 },
  field_map: {
    hostname: '$.name',
    serial:   '$.serialNumber',
    os:       '$.osVersion',
  },
  match_keys_map: {
    serial:   '$.serialNumber',
    hostname: '$.name',
  },
}

// Track 1.5 — SQL read-replica starter (PostgreSQL today; MSSQL/Oracle stubs).
// Snapshot-with-deltas pattern: $1 = since, $2 = page-size limit.
const STARTER_SPEC_SQL = {
  name: 'my-new-sql-source',
  display_name: 'My New SQL Source',
  archetype: 'asset_server',
  kind: 'sql',
  driver: 'postgresql',
  limit: 1000,
  since_param_index: 1,
  limit_param_index: 2,
  query: [
    'SELECT',
    '  id,',
    '  hostname,',
    '  serial_number,',
    '  last_modified',
    'FROM cmdb.asset_view',
    'WHERE last_modified >= $1',
    'ORDER BY last_modified ASC',
    'LIMIT $2',
  ].join('\n'),
  field_map: {
    hostname: 'hostname',
    serial:   'serial_number',
    last_seen:'last_modified',
  },
  match_keys_map: {
    serial:   'serial_number',
    hostname: 'hostname',
  },
  id_path: 'id',
  occurred_at_path: 'last_modified',
}

const STARTER_CONFIG_JSON = '{\n  "token": "<paste-token-here>"\n}'
const STARTER_CONFIG_SQL = '{\n  "connection_string": "postgresql://readonly_user:<pass>@host:5432/db"\n}'

export default function CdmSpecsPage() {
  const [specs, setSpecs] = useState<SpecSummary[]>([])
  const [loading, setLoading] = useState(false)
  const [editingName, setEditingName] = useState<string | null>(null)
  const [editorText, setEditorText] = useState('')
  const [editorConfig, setEditorConfig] = useState('')
  const [editorKind, setEditorKind] = useState<SpecKind>('json_api')
  const [message, setMessage] = useState('')
  const [testResult, setTestResult] = useState<any>(null)

  const load = () => {
    setLoading(true)
    api.get('/api/cdm/specs')
      .then(r => setSpecs(r.data?.specs || []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [])

  const startEdit = async (name: string | null) => {
    setEditingName(name)
    setTestResult(null)
    if (name === null) {
      // Default new-spec to JSON-API; user can flip to SQL via the radio.
      setEditorKind('json_api')
      setEditorText(JSON.stringify(STARTER_SPEC_JSON, null, 2))
      setEditorConfig(STARTER_CONFIG_JSON)
      return
    }
    try {
      const { data } = await api.get(`/api/cdm/specs/${name}`)
      const loadedKind: SpecKind = (data.spec?.kind === 'sql') ? 'sql' : 'json_api'
      setEditorKind(loadedKind)
      setEditorText(JSON.stringify(data.spec, null, 2))
      setEditorConfig('{}')
    } catch {
      setMessage(`Could not load spec "${name}"`)
    }
  }

  // Flip the starter when user toggles the kind radio (only meaningful for
  // new specs; once you've started typing we don't clobber your work).
  const switchKind = (next: SpecKind) => {
    setEditorKind(next)
    if (editingName !== null) return
    if (next === 'sql') {
      setEditorText(JSON.stringify(STARTER_SPEC_SQL, null, 2))
      setEditorConfig(STARTER_CONFIG_SQL)
    } else {
      setEditorText(JSON.stringify(STARTER_SPEC_JSON, null, 2))
      setEditorConfig(STARTER_CONFIG_JSON)
    }
  }

  const save = async () => {
    setMessage('Saving…')
    try {
      const spec = JSON.parse(editorText)
      const { data } = await api.post('/api/cdm/specs', {
        name: spec.name,
        display_name: spec.display_name || spec.name,
        archetype: spec.archetype || 'asset_laptop',
        spec,
        enabled: true,
      })
      setMessage(`Spec ${data.action}: ${data.name}`)
      load()
      setEditingName(spec.name)
    } catch (e: any) {
      setMessage(`Save failed: ${e.response?.data?.detail || e.message}`)
    }
  }

  const remove = async (name: string) => {
    if (!confirm(`Delete spec "${name}"? Adapter will stop being callable.`)) return
    try {
      await api.delete(`/api/cdm/specs/${name}`)
      setMessage(`Deleted ${name}`)
      if (editingName === name) setEditingName(null)
      load()
    } catch (e: any) {
      setMessage(`Delete failed: ${e.response?.data?.detail || e.message}`)
    }
  }

  const test = async () => {
    if (!editingName) return
    setTestResult(null)
    setMessage('Running dry-run preview…')
    try {
      let configObj = {}
      try { configObj = JSON.parse(editorConfig) } catch {}
      const { data } = await api.post(`/api/cdm/specs/${editingName}/test`, { config: configObj })
      setTestResult(data)
      setMessage(data.ok
        ? `Preview OK — ${data.preview?.length || 0} records would be staged`
        : `Preview failed: ${data.error || 'unknown'}`)
    } catch (e: any) {
      setMessage(`Test failed: ${e.response?.data?.detail || e.message}`)
    }
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <Link to="/app/cdm" className="text-text-muted hover:text-text-primary inline-flex items-center gap-1">
          <ChevronLeft size={14} /> CDM
        </Link>
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <Code size={18} className="text-cyan" /> CDM ADAPTER SPECS
        </h1>
        <span className="text-[11px] uppercase tracking-widest text-text-muted">
          declarative YAML/JSON specs · long-tail vendor coverage without Python
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button type="button" onClick={() => startEdit(null)} className="btn-ghost">
            <Plus size={11} /> New spec
          </button>
          <button type="button" onClick={load} disabled={loading} className="btn-ghost">
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {message && (
        <div className="text-[11px] text-text-secondary font-mono px-3 py-2 border border-border rounded bg-surface-card">
          {message}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Specs list */}
        <div className="card p-0 overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-surface-raised">
              <tr className="text-[10px] uppercase tracking-widest text-text-muted border-b border-border">
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">Kind</th>
                <th className="text-left px-3 py-2">Archetype</th>
                <th className="text-left px-3 py-2">Auth</th>
                <th className="text-right px-3 py-2">Fields</th>
                <th className="text-right px-3 py-2" aria-label="Actions"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {specs.length === 0 && !loading && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-text-muted">
                  No specs yet. Click 'New spec' to add one — or drop a YAML file
                  into <code>backend/cdm/specs/</code> on the server.
                </td></tr>
              )}
              {specs.map(s => {
                const k = (s.kind || 'json_api')
                return (
                <tr key={s.id} className={clsx(
                  'hover:bg-surface-raised/40',
                  editingName === s.name && 'bg-blue/5',
                )}>
                  <td className="px-3 py-1.5">
                    <button type="button" onClick={() => startEdit(s.name)}
                            className="text-blue-light hover:underline font-mono text-[11px]">
                      {s.name}
                    </button>
                    <div className="text-[10px] text-text-muted">{s.display_name}</div>
                  </td>
                  <td className="px-3 py-1.5">
                    <span className={clsx(
                      'inline-block text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded font-mono',
                      k === 'sql'
                        ? 'bg-purple/20 text-purple-light border border-purple/40'
                        : 'bg-blue/20 text-blue-light border border-blue/40',
                    )}>{k}</span>
                  </td>
                  <td className="px-3 py-1.5 text-text-secondary">{s.archetype}</td>
                  <td className="px-3 py-1.5 text-text-secondary">{s.auth_kind}</td>
                  <td className="px-3 py-1.5 text-right font-mono">{s.field_count}</td>
                  <td className="px-3 py-1.5 text-right">
                    <button type="button" onClick={() => remove(s.name)}
                            className="text-rose-light hover:text-rose-light/70" title="Delete">
                      <Trash2 size={12} />
                    </button>
                  </td>
                </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Editor */}
        <div className="card p-3 space-y-2">
          <div className="flex items-center gap-2">
            <FileCode size={14} className="text-cyan" />
            <h2 className="text-sm font-semibold text-text-primary">
              {editingName ? `Editing: ${editingName}` : 'New spec'}
            </h2>
            <div className="ml-auto flex items-center gap-2">
              {editingName && (
                <button type="button" onClick={test} className="btn-ghost text-[11px]"
                        title="Dry-run: pull one page, show first 3 records">
                  <Play size={11} /> Test
                </button>
              )}
              <button type="button" onClick={save} className="btn-primary text-[11px]" disabled={!editorText}>
                Save
              </button>
            </div>
          </div>

          {editingName === null && !editorText ? (
            <p className="text-text-muted text-xs">Click 'New spec' to start, or pick a row to edit.</p>
          ) : (
            <>
              <div className="flex items-center gap-3 text-[11px]">
                <span className="text-[10px] uppercase tracking-widest text-text-muted">Spec kind</span>
                <label className="inline-flex items-center gap-1 cursor-pointer">
                  <input
                    type="radio"
                    name="spec-kind"
                    value="json_api"
                    checked={editorKind === 'json_api'}
                    onChange={() => switchKind('json_api')}
                  />
                  <span className="text-blue-light font-mono">json_api</span>
                </label>
                <label className="inline-flex items-center gap-1 cursor-pointer">
                  <input
                    type="radio"
                    name="spec-kind"
                    value="sql"
                    checked={editorKind === 'sql'}
                    onChange={() => switchKind('sql')}
                  />
                  <span className="text-purple-light font-mono">sql</span>
                </label>
                {editingName !== null && (
                  <span className="text-[10px] text-text-muted ml-auto">
                    Existing spec — edit JSON below to change kind.
                  </span>
                )}
              </div>
              <div>
                <label htmlFor="spec-json" className="text-[10px] uppercase tracking-widest text-text-muted">Spec (JSON)</label>
                <textarea
                  id="spec-json"
                  value={editorText}
                  onChange={e => setEditorText(e.target.value)}
                  className="w-full font-mono text-[11px] bg-surface-raised p-2 rounded border border-border"
                  rows={20}
                  spellCheck={false}
                  placeholder="Paste or edit the spec JSON here"
                />
              </div>
              <div>
                <label htmlFor="spec-config" className="text-[10px] uppercase tracking-widest text-text-muted">
                  Test config ({editorKind === 'sql' ? 'connection_string for dry-run' : 'auth values for dry-run'})
                </label>
                <textarea
                  id="spec-config"
                  value={editorConfig}
                  onChange={e => setEditorConfig(e.target.value)}
                  className="w-full font-mono text-[11px] bg-surface-raised p-2 rounded border border-border"
                  rows={3}
                  spellCheck={false}
                  placeholder={editorKind === 'sql'
                    ? '{"connection_string": "postgresql://..."}'
                    : '{"token": "..."}'}
                />
              </div>
            </>
          )}

          {testResult && (
            <div className="mt-2">
              <div className="text-[10px] uppercase tracking-widest text-text-muted mb-1">
                Preview ({testResult.ok ? '✓' : '✗'})
              </div>
              <pre className="text-[10px] font-mono text-text-secondary bg-surface-raised p-2 rounded border border-border overflow-auto max-h-64">
                {JSON.stringify(testResult, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </div>

      <p className="text-[10px] text-text-muted text-center">
        Specs become full CDM adapters — the runner can call them by name like any built-in adapter.
        Saved specs override shipped YAML files with the same name.
      </p>
    </div>
  )
}
